import { useEffect, useState } from 'react';
import { Tooltip, UnstyledButton, Box } from '@mantine/core';
import { checkSupport, getSubscription, subscribePush } from '../../push/client.js';
import { ACCENT, MUTED, TEXT } from '../../theme/tokens.js';

/**
 * Компактное включение push-уведомлений для верхней панели.
 *
 * Заменил прежний `PushToggle` со старой главной (удалён вместе с ней): в панели
 * высотой 64px нужна иконка 44×44, а не широкая кнопка с текстом и `Alert` под
 * ней. Логика подписки та же — общий `push/client.js`.
 *
 * Кнопка исчезает, когда подписка уже оформлена или push недоступен: включать
 * нечего, а место в панели дорогое.
 */
export function PushButton() {
  const sup = checkSupport();
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (!sup.ok) return;
    void getSubscription().then((s) => setSubscribed(!!s));
  }, [sup.ok]);

  // Push не поддерживается (нет HTTPS / старый браузер) либо уже включён.
  if (!sup.ok || subscribed !== false) return null;

  const turnOn = async () => {
    setBusy(true);
    setFailed(null);
    const r = await subscribePush();
    setBusy(false);
    if (r.ok) setSubscribed(true);
    else setFailed(r.reason);
  };

  const label = failed ? `Не удалось включить уведомления: ${failed}` : 'Включить push-уведомления';

  return (
    <Tooltip label={label} withArrow position="bottom">
      <UnstyledButton
        onClick={() => void turnOn()}
        disabled={busy}
        aria-label={label}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
          color: failed ? '#E03131' : MUTED, opacity: busy ? 0.5 : 1,
        }}
        onMouseEnter={(e) => { if (!busy) e.currentTarget.style.color = ACCENT; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = failed ? '#E03131' : MUTED; }}
      >
        <Box style={{ display: 'flex' }}>
          <svg
            width={21} height={21} viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden
          >
            <path d="M18 8.5a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16s-2-1.5-2-6.5Z" />
            <path d="M10.3 19a2 2 0 0 0 3.4 0" />
            <path d="m4.5 4.5 15 15" stroke={TEXT} strokeOpacity="0.35" />
          </svg>
        </Box>
      </UnstyledButton>
    </Tooltip>
  );
}
